import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { showToast } from './Toast';
import { formatINR } from '../utils/priceFormatter';

const ProductModal = ({ 
  isOpen, 
  product, 
  onClose, 
  onAddToCart 
}) => {
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [showGoToCart, setShowGoToCart] = useState(false);
  
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const sizeSelectionRef = React.useRef(null);

  useEffect(() => {
    if (isOpen && product) {
      setSelectedSize("");
      setSelectedColor(product.colors?.[0] || "");
      setQuantity(1);
      setShowGoToCart(false);
      document.body.style.overflow = 'hidden';
      
      // If opened via "Add to Cart" button, scroll to size selection
      if (product.focusOnSize && sizeSelectionRef.current) {
        setTimeout(() => {
          sizeSelectionRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          // Add a subtle highlight effect
          sizeSelectionRef.current?.classList.add('highlight-section');
          setTimeout(() => {
            sizeSelectionRef.current?.classList.remove('highlight-section');
          }, 2000);
        }, 300);
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, product]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const handleGoToCart = () => {
    navigate('/cart');
    onClose(); // Close the modal when navigating to cart
  };

  const handleAddToCart = async () => {
    if (!selectedSize && product.sizes && product.sizes.length > 0) {
      showToast('Please select a size', 'error');
      return;
    }
    
    setIsAddingToCart(true);
    
    try {
      // Simulate API call delay for better UX
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Add to cart context
      const success = addToCart(product, selectedSize, selectedColor, quantity);
      
      if (success) {
        setAddedToCart(true);
        showToast(`${product.name} added to cart!`, 'success');
        
        // Call original onAddToCart if provided (for backward compatibility)
        if (onAddToCart) {
          const cartItem = {
            product,
            size: selectedSize,
            color: selectedColor,
            quantity: quantity
          };
          onAddToCart(cartItem);
        }
        
        // Show "Go to Cart" state after success animation
        setTimeout(() => {
          setAddedToCart(false);
          setIsAddingToCart(false);
          setShowGoToCart(true);
        }, 2000);
      }
    } catch (error) {
      showToast('Failed to add item to cart', 'error');
      setIsAddingToCart(false);
    }
  };

  const Stars = ({ rating }) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    return (
      <StarsContainer>
        {[...Array(5)].map((_, i) => (
          <Star key={i} filled={i < fullStars || (i === fullStars && hasHalfStar)}>
            ★
          </Star>
        ))}
        <RatingNumber>{rating}</RatingNumber>
      </StarsContainer>
    );
  };

  if (!isOpen || !product) return null;

  return (
    <Modal onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalClose onClick={onClose}>×</ModalClose>
        <ModalBody>
          <ModalImageSection>
            <ModalImage src={product.image} alt={product.name} />
            {product.badge && <ModalBadge>{product.badge}</ModalBadge>}
          </ModalImageSection>
          <ModalInfoSection>
            <ModalCategory>{product.category}</ModalCategory>
            <ModalTitle>{product.name}</ModalTitle>
            <ModalRating>
              <Stars rating={product.rating || 4.5} />
              <span>({product.reviews || 0} reviews)</span>
            </ModalRating>
            <ModalPrice>{formatINR(product.price)}</ModalPrice>
            <ModalDescription>{product.description}</ModalDescription>
            
            {/* Color Selection */}
            {product.colors && product.colors.length > 0 && (
              <ModalSection>
                <ModalSectionTitle>Color</ModalSectionTitle>
                <ColorOptions>
                  {product.colors.map(color => (
                    <ColorOption
                      key={color}
                      active={selectedColor === color}
                      onClick={() => setSelectedColor(color)}
                    >
                      {color}
                    </ColorOption>
                  ))}
                </ColorOptions>
              </ModalSection>
            )}

            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <ModalSection ref={sizeSelectionRef}>
                <ModalSectionTitle>Size *</ModalSectionTitle>
                <SizeOptions>
                  {product.sizes.map(size => (
                    <SizeOption
                      key={size}
                      active={selectedSize === size}
                      onClick={() => setSelectedSize(size)}
                    >
                      {size}
                    </SizeOption>
                  ))}
                </SizeOptions>
                {!selectedSize && <RequiredText>Please select a size</RequiredText>}
              </ModalSection>
            )}

            {/* Quantity Selection */}
            <ModalSection>
              <ModalSectionTitle>Quantity</ModalSectionTitle>
              <QuantitySelector>
                <QuantityButton 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </QuantityButton>
                <QuantityInput 
                  type="number" 
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                  max="10"
                />
                <QuantityButton 
                  onClick={() => setQuantity(Math.min(10, quantity + 1))}
                  disabled={quantity >= 10}
                >
                  +
                </QuantityButton>
              </QuantitySelector>
            </ModalSection>

            {/* Product Features */}
            <ModalFeatures>
              <FeatureItem>Free shipping on orders over $50</FeatureItem>
              <FeatureItem>30-day return policy</FeatureItem>
              <FeatureItem>Nike+ member exclusive</FeatureItem>
            </ModalFeatures>
            
            <ModalActions>
              <ModalAddToCart 
                onClick={showGoToCart ? handleGoToCart : handleAddToCart}
                disabled={(product.sizes && product.sizes.length > 0 && !selectedSize) || isAddingToCart}
                isLoading={isAddingToCart}
                isSuccess={addedToCart}
                isGoToCart={showGoToCart}
              >
                {isAddingToCart ? (
                  <>
                    <ButtonSpinner />
                    Adding to Cart...
                  </>
                ) : addedToCart ? (
                  <>
                    <SuccessIcon>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                    </SuccessIcon>
                    Added to Cart!
                  </>
                ) : showGoToCart ? (
                  <>
                    <CartIcon>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="9" cy="21" r="1"></circle>
                        <circle cx="20" cy="21" r="1"></circle>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                      </svg>
                    </CartIcon>
                    Go to Cart
                  </>
                ) : (
                  `Add to Cart - $${product.price * quantity}`
                )}
              </ModalAddToCart>
              <ModalWishlist>♡ Save</ModalWishlist>
            </ModalActions>
          </ModalInfoSection>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ProductModal;

// Styled Components
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 1000px;
  width: 100%;
  max-height: 95vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-height: 100vh;
    border-radius: 0;
  }
`;

const ModalClose = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  z-index: 1;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ModalImageSection = styled.div`
  flex: 0.8;
  position: relative;
  background: #f8f8f8;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    min-height: 250px;
    flex: none;
  }
`;

const ModalImage = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const ModalBadge = styled.span`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(17, 17, 17, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const ModalInfoSection = styled.div`
  flex: 1.2;
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 20px;
    flex: 1;
  }
`;

const ModalCategory = styled.span`
  font-size: 14px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 6px 0 12px 0;
  color: #111;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin: 4px 0 8px 0;
  }
`;

const ModalRating = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  
  span {
    color: #666;
    font-size: 13px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
    gap: 6px;
  }
`;

const StarsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Star = styled.span`
  color: ${props => props.filled ? '#ffc107' : '#e0e0e0'};
  font-size: 14px;
`;

const RatingNumber = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 600;
  margin-left: 4px;
`;

const ModalPrice = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #111;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 10px;
  }
`;

const ModalDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: #666;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 12px;
  }
`;

// Animation keyframes
const highlightPulse = keyframes`
  0% { 
    background: rgba(255, 235, 59, 0.1);
    border-left-color: #ffc107;
  }
  50% { 
    background: rgba(255, 235, 59, 0.2);
    border-left-color: #ff9800;
  }
  100% { 
    background: rgba(255, 235, 59, 0.1);
    border-left-color: #ffc107;
  }
`;

const ModalSection = styled.div`
  margin-bottom: 16px;
  transition: all 0.3s ease;
  
  &.highlight-section {
    background: rgba(255, 235, 59, 0.1);
    padding: 12px;
    border-radius: 6px;
    border-left: 3px solid #ffc107;
    animation: ${highlightPulse} 2s ease-out;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const ModalSectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: #111;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin: 0 0 6px 0;
  }
`;

const ColorOptions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ColorOption = styled.button`
  padding: 6px 12px;
  border: 1px solid ${props => props.active ? '#111' : '#e0e0e0'};
  background: ${props => props.active ? '#111' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #111;
    background: ${props => props.active ? '#111' : '#f8f8f8'};
  }
  
  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 11px;
  }
`;

const SizeOptions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const SizeOption = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid ${props => props.active ? '#111' : '#e0e0e0'};
  background: ${props => props.active ? '#111' : 'white'};
  color: ${props => props.active ? 'white' : '#666'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #111;
    background: ${props => props.active ? '#111' : '#f8f8f8'};
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 12px;
  }
`;

const RequiredText = styled.p`
  font-size: 12px;
  color: #ff4757;
  margin: 0;
  font-style: italic;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  width: fit-content;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    border-radius: 3px;
  }
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: white;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f8f8f8;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:first-child {
    border-right: 1px solid #e0e0e0;
  }
  
  &:last-child {
    border-left: 1px solid #e0e0e0;
  }
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  height: 32px;
  border: none;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  background: white;
  
  &:focus {
    outline: none;
  }
  
  /* Hide number input arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 28px;
    font-size: 12px;
  }
`;

const ModalFeatures = styled.div`
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const FeatureItem = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  padding-left: 14px;
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4caf50;
    font-size: 11px;
    font-weight: bold;
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
    margin-bottom: 3px;
    padding-left: 12px;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: auto;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ModalAddToCart = styled.button`
  flex: 1;
  background: ${props => {
    if (props.disabled && !props.isLoading && !props.isSuccess && !props.isGoToCart) return '#ccc';
    if (props.isGoToCart) return '#2980b9';
    if (props.isSuccess) return '#27ae60';
    if (props.isLoading) return '#666';
    return '#111';
  }};
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
  
  &:hover:not(:disabled) {
    background: ${props => {
      if (props.isGoToCart) return '#1f5582';
      if (props.isSuccess) return '#229954';
      if (props.isLoading) return '#666';
      return '#000';
    }};
    transform: translateY(-1px);
  }
  
  ${props => props.isSuccess && css`
    animation: ${successPulse} 0.6s ease-out;
  `}
  
  ${props => props.isGoToCart && css`
    animation: ${goToCartPulse} 1s ease-in-out infinite;
  `}
`;

const ModalWishlist = styled.button`
  background: white;
  border: 2px solid #e0e0e0;
  color: #666;
  padding: 16px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #ff4757;
    color: #ff4757;
  }
`;

// Animation keyframes
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const successPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const goToCartPulse = keyframes`
  0% { 
    box-shadow: 0 0 0 0 rgba(41, 128, 185, 0.4);
  }
  70% { 
    box-shadow: 0 0 0 10px rgba(41, 128, 185, 0);
  }
  100% { 
    box-shadow: 0 0 0 0 rgba(41, 128, 185, 0);
  }
`;

// Button animation components
const ButtonSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const SuccessIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

const CartIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;
